/**
 * The due list: every claim in this library that promised an answer by a date
 * and has no result yet.
 *
 * Read from stored documents through `core/resolution.ts` — a claim leaves the
 * list by having an outcome recorded against it, never by the date passing —
 * and it calls no model and offers no press that spends. Overdue rows lead,
 * because an overdue promise is the most useful row here: it is the one place
 * this app can say "you said you would know by now" without inventing a
 * judgement.
 *
 * Rebuilt when the library changes rather than on a timer. There is no reminder
 * and no notification, deliberately: this is a list a person reads when they
 * open the app, not a thing that chases them.
 */

import { useEffect, useMemo, useState } from 'react'
import { libraryPromiseSources, resolutionQueue, withLive } from '../core/resolution'
import type { ResolutionSource } from '../core/resolution'
import { shelfKeyExcept } from '../core/persist'
import { openSession } from '../core/autosave'
import { useStore } from '../core/store'
import { useDocs } from './useDocs'
import { Icon } from './components/Icon'

/**
 * A LOCAL calendar day, not a UTC one.
 *
 * The stored value is local midday on the day the person picked, so a UTC
 * rendering is off by one wherever the offset is large enough to cross
 * midnight: at UTC+13 picking 3 September stores 2 September UTC, and the date
 * input — which reads and writes local days — would redisplay the day before
 * the one just chosen. `toISOString` is right for an artefact somebody else
 * reads and wrong for the field the person typed in.
 */
const day = (t: number): string => {
  const d = new Date(t)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
const days = (ms: number) => Math.round(Math.abs(ms) / 86_400_000)

/**
 * The stored half of the queue, cached on the rows of every OTHER exploration.
 *
 * The same shape and the same argument as `claimIndexFor` in `Standing.tsx`,
 * which worked this out first: the memo above may re-run whenever the shelf
 * announces itself — that is the subscription doing its job — while this decides
 * whether there is new work. `shelfKeyExcept` is one small localStorage row per
 * exploration; what it avoids is unpacking and parsing every stored document.
 *
 * Keyed on the excluded id as well, because switching exploration changes which
 * document is left out and two libraries can otherwise agree on every row.
 */
let sourcesCache: { key: string; rows: ResolutionSource[] } | null = null
function promiseSourcesExcept(sessionId: string): ResolutionSource[] {
  const key = `${sessionId}\u0000${shelfKeyExcept(sessionId)}`
  if (sourcesCache?.key !== key) {
    sourcesCache = { key, rows: libraryPromiseSources(sessionId) }
  }
  return sourcesCache.rows
}

export function DueList() {
  const rev = useStore((s) => s.rev)
  const session = useStore((s) => s.session)
  // And the REST of the shelf, subscribed: a date typed or a result recorded in
  // another tab moves neither `rev` nor `session`, so without this the list
  // lagged every exploration but the open one until an unrelated local edit.
  const shelf = useDocs()
  /**
   * And an advancing clock, because `overdue` is a fact about NOW.
   *
   * None of the other dependencies represents time, so a tab left open across a
   * deadline kept printing "in 1 day" and withholding the overdue badge until
   * some unrelated edit recomputed the list. The row's whole claim is "you said
   * you would know by now", and it was the one thing the memo could not notice.
   *
   * Woken at the next deadline rather than on an interval: a list with nothing
   * due schedules nothing, and one with a deadline tonight wakes once, tonight.
   * A day is the ceiling, since `setTimeout` overflows past ~24.8 of them and a
   * silent overflow fires immediately, forever.
   *
   * `now` is in the dependencies BECAUSE of that ceiling. A deadline further out
   * than a day is woken at the cap, which moves `now` and leaves `nextDue` — the
   * same absolute instant — untouched, so keying the effect on `nextDue` alone
   * armed the first day's timer and never a second. The promise then came due
   * against a tab that had stopped counting. The card in `Standing.tsx` keeping
   * this same clock had `[due, now]` from the start; this is the sibling that
   * did not, which is the whole of the bug.
   */
  const [now, setNow] = useState(() => Date.now())
  const [open, setOpen] = useState(false)
  // TWO memos, because the two halves change at different rates and one of them
  // is expensive. Reading and inflating every stored exploration is keyed on the
  // shelf ROWS OF EVERY OTHER EXPLORATION — `shelfKeyExcept`, which is what this
  // half actually consumes.
  //
  // Neither obvious key works, and this list used both before arriving here.
  // `rev` moves on a pane toggle through `setViewInset`, which swaps no document
  // and no claim. The shelf revision moves on the OPEN document's own autosave,
  // and the open document is the one whose stored copy `withLive` throws away —
  // so an ordinary keystroke re-read and re-inflated every OTHER exploration to
  // produce a list that could not have changed. `Standing.tsx` had written this
  // argument down for the claim index before either mistake was made here.
  //
  // `shelf` stays as the SUBSCRIPTION — a save in another tab is what has to
  // wake this component — while the key decides whether there is new work.
  const stored = useMemo(() => promiseSourcesExcept(session.id), [session.id, shelf])
  // The fold itself is cheap — no parsing, just the rows — so it keeps `rev`
  // beside `session`: the OPEN exploration comes from memory and the rest from
  // the shelf, and `rev` changes the moment a date is typed while the autosave
  // is over a second behind it. Reading that one off disk too would leave a
  // promise just made missing from the list and a result just recorded still on
  // it.
  const rows = useMemo(
    () => resolutionQueue(withLive(stored, session), now),
    [stored, rev, session, now],
  )
  // The soonest deadline still ahead of us, or nothing.
  const nextDue = rows.reduce<number | null>(
    (soonest, r) =>
      r.resolvesBy > now && (soonest === null || r.resolvesBy < soonest) ? r.resolvesBy : soonest,
    null,
  )
  useEffect(() => {
    if (nextDue === null) return
    const id = setTimeout(
      () => setNow(Date.now()),
      Math.min(nextDue - Date.now() + 1000, 86_400_000),
    )
    return () => clearTimeout(id)
  }, [nextDue, now])
  if (!rows.length) return null
  const overdue = rows.filter((r) => r.overdue).length

  return (
    <div className="duelist">
      <button
        className="duelist-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Claims across every exploration on this device that promised an answer and have none recorded"
      >
        <Icon name="experiment" size={12} />
        <b>
          {rows.length} claim{rows.length === 1 ? '' : 's'} due
        </b>
        {overdue > 0 && <span className="duelist-over">{overdue} overdue</span>}
        <Icon
          name="chevron"
          size={9}
          style={{ marginLeft: 'auto', transform: open ? 'rotate(-90deg)' : 'rotate(90deg)' }}
        />
      </button>
      {open && (
        <div className="duelist-body">
          {rows.slice(0, 12).map((r) => (
            <button
              key={`${r.sessionId}:${r.nodeId}`}
              className="duelist-row"
              data-over={r.overdue || undefined}
              title={r.claim}
              onClick={() => openSession(r.sessionId, r.nodeId)}
            >
              <b>{r.label}</b>
              <span>
                {r.overdue
                  ? `${days(r.dueInMs)} day${days(r.dueInMs) === 1 ? '' : 's'} overdue`
                  : `due ${day(r.resolvesBy)}`}{' '}
                · {r.seed}
              </span>
            </button>
          ))}
          {rows.length > 12 && (
            <p className="gnote gnote--quiet">
              {rows.length - 12} more, further out. The nearest twelve are shown.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
